import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Play, Pause, SkipForward, SkipBack, Music, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useMusicKit } from '../../context/MusicKitContext';

/**
 * SimpleSongBar - "SIMPLE SONG BAR" design
 * Used for: DMs, Home Search, Event Recap
 * Features: Basic play controls only (NO queue, NO fire, NO fingerprint)
 * Reference name: "Simple Song Bar" or "Mini Bar"
 */
const SimpleSongBar = ({ onClose }) => {
  const { isDarkMode } = useTheme();
  
  // Persist minimized state
  const [isMinimized, setIsMinimized] = useState(() => {
    try {
      return localStorage.getItem('tapInSimpleSongBarMinimized') === 'true';
    } catch {
      return false;
    }
  });
  
  useEffect(() => {
    try {
      localStorage.setItem('tapInSimpleSongBarMinimized', isMinimized.toString());
    } catch {}
  }, [isMinimized]);

  const {
    isPlaying: musicKitIsPlaying,
    currentSong: musicKitCurrentSong,
    play: musicKitPlay,
    pause: musicKitPause,
    playbackTime,
    duration,
    seekTo,
    stop: stopMusicKit,
  } = useMusicKit();

  const isPlaying = musicKitIsPlaying;

  // Display song info
  const displaySong = useMemo(() => {
    if (musicKitCurrentSong) {
      let artworkUrl = musicKitCurrentSong.artworkUrl || '';
      if (artworkUrl && artworkUrl.includes('{w}')) {
        artworkUrl = artworkUrl.replace('{w}', '300').replace('{h}', '300');
      }
      return {
        title: musicKitCurrentSong.name,
        artist: musicKitCurrentSong.artistName,
        artwork_url: artworkUrl,
        id: musicKitCurrentSong.id
      };
    }
    return null;
  }, [musicKitCurrentSong]);

  // Progress bar
  const progressPercent = duration > 0 ? (playbackTime / duration) * 100 : 0;
  
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Handlers
  const handleTogglePlayPause = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (isPlaying) {
      musicKitPause();
    } else {
      musicKitPlay();
    }
  }, [isPlaying, musicKitPlay, musicKitPause]);

  const handleSeek = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    if (seekTo && duration > 0) {
      seekTo(newTime);
    }
  }, [seekTo, duration]);

  // Don't show if no song at all
  if (!displaySong) return null;

  // Minimized view - never show music note default
  if (isMinimized) {
    if (!displaySong?.artwork_url) return null;
    return (
      <div className="fixed bottom-6 right-4 z-50" data-testid="simple-song-bar-minimized">
        <button
          onClick={() => setIsMinimized(false)}
          className="w-11 h-11 rounded-full overflow-hidden transition-all hover:scale-110 active:scale-95 relative"
          style={{
            boxShadow: isDarkMode 
              ? '0 0 15px rgba(168, 85, 247, 0.5), 0 0 30px rgba(168, 85, 247, 0.2)' 
              : '0 0 15px rgba(168, 85, 247, 0.4)',
          }}
        >
          <img src={displaySong.artwork_url} alt={displaySong?.title} className="w-full h-full object-cover" />
          {isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="flex gap-0.5">
                <div className="w-0.5 h-2.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50" data-testid="simple-song-bar">
      <div className="max-w-[430px] mx-auto px-3">
        <div 
          className={`mb-3 rounded-xl overflow-visible relative`}
          style={{
            background: isDarkMode ? 'rgba(55, 55, 60, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            boxShadow: isDarkMode 
              ? '0 0 20px rgba(168, 85, 247, 0.3), 0 -2px 15px rgba(0,0,0,0.3)' 
              : '0 -4px 20px rgba(0,0,0,0.1)',
          }}
        >
          {/* X button - top left corner with large tap target */}
          <button 
            onClick={(e) => {
              e?.stopPropagation();
              e?.preventDefault();
              setIsMinimized(true);
            }}
            className={`absolute -top-2 -left-1 w-6 h-6 rounded-full flex items-center justify-center z-10 ${
              isDarkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 active:bg-gray-500' 
                : 'bg-gray-300 hover:bg-gray-400 text-gray-600 active:bg-gray-500'
            }`}
            style={{ touchAction: 'manipulation' }}
            data-testid="simple-song-bar-close"
          >
            <X className="w-3 h-3" strokeWidth={2.5} />
          </button>
          
          <div className="px-2 py-2 flex items-center gap-1.5">
            {/* Album art */}
            <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 ml-4">
              {displaySong?.artwork_url ? (
                <img src={displaySong.artwork_url} alt={displaySong?.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-600 rounded-lg flex items-center justify-center">
                  <Music className="w-4 h-4 text-gray-400" />
                </div>
              )}
            </div>
            
            {/* Song info */}
            <div className="flex-1 min-w-0">
              <p className={`font-medium truncate text-xs ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {displaySong?.title || 'No song playing'}
              </p>
              <p className={`text-xs truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {displaySong?.artist || ''}
              </p>
            </div>
            
            {/* Previous */}
            <button 
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 opacity-30 cursor-not-allowed`}
              disabled
              data-testid="simple-song-bar-prev"
            >
              <SkipBack className={`w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} fill="currentColor" />
            </button>
            
            {/* Play/Pause */}
            <button 
              onClick={handleTogglePlayPause}
              className="w-10 h-10 aspect-square rounded-full flex items-center justify-center flex-shrink-0 active:scale-90 transition-all bg-[#6366f1] text-white"
              style={{ minWidth: '40px', minHeight: '40px', touchAction: 'manipulation' }}
              data-testid="simple-song-bar-play-pause"
            >
              {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
            </button>
            
            {/* Next */}
            <button 
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 opacity-30 cursor-not-allowed`}
              disabled
              data-testid="simple-song-bar-next"
            >
              <SkipForward className={`w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} fill="currentColor" />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="px-2 pb-1.5">
            <div className="flex items-center gap-1.5">
              <span className={`text-[9px] font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {formatTime(playbackTime)}
              </span>
              <div 
                className={`flex-1 h-1 rounded-full cursor-pointer ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                onClick={handleSeek}
                data-testid="simple-song-bar-progress"
              >
                <div 
                  className={`h-full rounded-full transition-all duration-100 ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className={`text-[9px] font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {formatTime(duration)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleSongBar;
